/**
 * App Routes
 * Main routing configuration with role-based protection
 */

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ProtectedRoute } from './ProtectedRoute';

// Public Pages
import Home from '../pages/public/Home';
import Courses from '../pages/public/Courses';
import CourseDetail from '../pages/public/CourseDetail';
import Jobs from '../pages/public/Jobs';
import Profile from '../pages/public/Profile';
import Login from '../pages/auth/Login';
import Signup from '../pages/auth/Signup';
import ForgotPassword from '../pages/auth/ForgotPassword';
import ResetPassword from '../pages/auth/ResetPassword';

// Student Pages
import StudentDashboard from '../pages/student/Dashboard';
import StudentCourses from '../pages/student/Courses';
import StudentCourseDetails from '../pages/student/CourseDetails';
import StudentLiveClass from '../pages/student/LiveClass';
import StudentPaymentHistory from '../pages/student/PaymentHistory';

// Teacher Pages
import TeacherDashboard from '../pages/teacher/Dashboard';
import CreateCourse from '../pages/teacher/CreateCourse';
import UploadLessons from '../pages/teacher/UploadLessons';

// Admin Pages
import AdminDashboard from '../pages/admin/Dashboard';
import UserManagement from '../pages/admin/UserManagement';
import CourseManagement from '../pages/admin/CourseManagement';
import PaymentManagement from '../pages/admin/PaymentManagement';
import SubscriptionManagement from '../pages/admin/SubscriptionManagement';

const AppRoutes = () => {
  return (
    <Routes>
      {/* ========== Public Routes ========== */}
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      
      {/* Public course browsing (can be accessed without login) */}
      <Route path="/courses" element={<Courses />} />
      <Route path="/courses/:id" element={<CourseDetail />} />
      <Route path="/jobs" element={<Jobs />} />
      
      {/* Profile Settings - Protected */}
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />

      {/* ========== Student Routes ========== */}
      <Route
        path="/student/dashboard"
        element={
          <ProtectedRoute allowedRoles={['student']}>
            <StudentDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/student/courses"
        element={
          <ProtectedRoute allowedRoles={['student']}>
            <StudentCourses />
          </ProtectedRoute>
        }
      />
      <Route
        path="/student/courses/:courseId"
        element={
          <ProtectedRoute allowedRoles={['student']}>
            <StudentCourseDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/student/courses/:courseId/lesson/:lessonId"
        element={
          <ProtectedRoute allowedRoles={['student']}>
            <StudentCourseDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/student/live-classes"
        element={
          <ProtectedRoute allowedRoles={['student']}>
            <StudentLiveClass />
          </ProtectedRoute>
        }
      />
      <Route
        path="/student/payments"
        element={
          <ProtectedRoute allowedRoles={['student']}>
            <StudentPaymentHistory />
          </ProtectedRoute>
        }
      />

      {/* ========== Teacher Routes ========== */}
      <Route
        path="/teacher/dashboard"
        element={
          <ProtectedRoute allowedRoles={['teacher']}>
            <TeacherDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/teacher/courses"
        element={
          <ProtectedRoute allowedRoles={['teacher']}>
            <TeacherDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/courses/create"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <CreateCourse />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/courses/edit/:id"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <CreateCourse />
          </ProtectedRoute>
        }
      />
      <Route
        path="/teacher/courses/:courseId"
        element={
          <ProtectedRoute allowedRoles={['teacher']}>
            <TeacherDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/teacher/courses/:courseId/lessons"
        element={
          <ProtectedRoute allowedRoles={['teacher']}>
            <UploadLessons />
          </ProtectedRoute>
        }
      />
      <Route
        path="/teacher/classes"
        element={
          <ProtectedRoute allowedRoles={['teacher']}>
            <TeacherDashboard />
          </ProtectedRoute>
        }
      />

      {/* ========== Admin Routes ========== */}
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <UserManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users/:userId"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <UserManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/courses"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <CourseManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/courses/:courseId"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <CourseManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/payments"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <PaymentManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/subscriptions"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <SubscriptionManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />

      {/* ========== Catch-all Redirect ========== */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default AppRoutes;
